/**
 * Region-specific SUPPLEMENTARY red flags (B2 — scored in the separate
 * supplementary pass, never summed with the universal escalation pass).
 *
 * These are the 31 ClinHx-only items RETAINED by the signed B2 decision record
 * (docs/B2_red_flag_alignment_DECISION_RECORD_signed_2026-07-11.docx, §1.3).
 * The 3 dropped items (redFlagWeakness/CONCUSSION, spreadingRedness/HAND,
 * ageOver50NewOnset/LUMBAR) are intentionally ABSENT. Items whose concept is
 * absorbed by the universal screen are not here either — the universal screen
 * covers them.
 *
 * Wording is the EXISTING verbatim wording of each retained item (no
 * rewording per the record): sourced from each region's red-flag definition
 * (full configs) or its template.fields patient question (terse configs). Legacy
 * weights are unchanged. `unableToWeightBear` recurs in PELVIS/THIGH/TIBIA with
 * that region's own existing wording — one distinct decision item, three
 * regions, hence 33 rows for 31 distinct items.
 *
 * Thresholds are the PROPOSED supplementary thresholds pending Dr Armstrong's
 * approval (see docs/b2-supplementary-thresholds-proposal.md). They are used by
 * the supplementary pass in utils/redFlagChecker.js. This module must NOT be
 * merged/deployed until those thresholds are approved.
 *
 * This is a documentation/data module ported to ClinHx conventions; it carries
 * no dependency on ara-health (these items are ClinHx-only content).
 */

const YN = ['Yes', 'No'];

export const SUPPLEMENTARY_RED_FLAGS = {
  CONCUSSION: {
    thresholds: { critical: 8, urgent: 7, concerning: 7 },
    items: [
      { id: 'redFlagHeadache', label: 'Is your headache getting worse over time?', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'redFlagVomiting', label: 'Have you vomited more than once?', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'redFlagSeizure', label: 'Have you had any seizures (fits) since the injury?', weight: 10, urgent: true, type: 'radio', options: YN },
      { id: 'redFlagSpeech', label: 'Is your speech slurred or hard to understand?', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'redFlagPupils', label: 'Do your pupils (the black part of your eyes) look different sizes?', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No', 'Not sure'] },
      { id: 'redFlagDrowsiness', label: 'Are you getting more and more drowsy or hard to wake up?', weight: 9, urgent: true, type: 'radio', options: YN },
      { id: 'redFlagNeckPain', label: 'Do you have bad neck pain?', weight: 7, urgent: false, type: 'radio', options: YN },
      { id: 'redFlagFluid', label: 'Is there clear fluid coming from your nose or ears?', weight: 10, urgent: true, type: 'radio', options: YN },
    ],
  },
  RIB: {
    thresholds: { critical: 8, urgent: 6, concerning: 5 },
    items: [
      { id: 'shortnessOfBreath', label: 'Shortness of breath', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'coughingBlood', label: 'Coughing up blood', weight: 10, urgent: true, type: 'radio', options: YN },
      { id: 'severeChestPain', label: 'Severe chest pain that does not go away', weight: 10, urgent: true, type: 'radio', options: YN },
      { id: 'cardiacRadiation', label: 'Pain spreading to jaw, arm, or back', weight: 8, urgent: true, type: 'radio', options: YN },
    ],
  },
  THORACIC_SPINE: {
    thresholds: { critical: 10, urgent: 6, concerning: 5 },
    items: [
      { id: 'chestPain', label: 'Do you have any chest pain?', weight: 7, urgent: true, type: 'radio', options: YN },
      { id: 'breathingProblems', label: 'Do you have any breathing problems?', weight: 6, urgent: false, type: 'radio', options: YN },
    ],
  },
  THIGH: {
    thresholds: { critical: 8, urgent: 6, concerning: 5 },
    items: [
      { id: 'tenseSwollenThigh', label: 'Does your thigh feel very tight, hard, and swollen?', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'darkUrine', label: 'Is your urine (pee) very dark, like cola or tea?', weight: 6, urgent: true, type: 'radio', options: YN },
      { id: 'severeIncreasingPain', label: 'Is your thigh pain very bad and getting worse even with rest?', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'unableToWeightBear', label: 'Are you unable to put weight on your leg?', weight: 5, urgent: false, type: 'radio', options: YN },
    ],
  },
  TIBIA: {
    // Approved 2026-07-12. CRITICAL raised to 13 (from 8) with a single-item
    // carve-out: calfSwellingDVT (DVT) alone -> CRITICAL. This avoids benign
    // combinations (e.g. numbnessAfterRunning + unableToWeightBear = 12)
    // over-escalating to CRITICAL while still treating a suspected DVT as
    // critical on its own.
    thresholds: { critical: 13, urgent: 6, concerning: 5 },
    criticalItems: ['calfSwellingDVT'],
    items: [
      { id: 'calfSwellingDVT', label: 'Is your calf swollen, warm, and painful (especially one side only)?', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'numbnessAfterRunning', label: 'Do you get numbness or tingling in your foot that lasts after you stop running?', weight: 6, urgent: true, type: 'radio', options: YN },
      { id: 'progressiveSymptoms', label: 'Are your symptoms getting worse even though you have stopped running?', weight: 4, urgent: false, type: 'radio', options: YN },
      { id: 'unableToWeightBear', label: 'Are you unable to put weight on your leg?', weight: 6, urgent: true, type: 'radio', options: YN },
    ],
  },
  HAND: {
    thresholds: { critical: 8, urgent: 6, concerning: 5 },
    items: [
      { id: 'fingerColourChange', label: 'Has the finger changed colour (gone white or blue)?', weight: 10, urgent: true, type: 'radio', options: YN },
      { id: 'highPressureInjection', label: 'Was this a high-pressure injection injury (paint gun, grease gun)?', weight: 10, urgent: true, type: 'radio', options: YN },
      { id: 'openWound', label: 'Is there an open wound?', weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'fightBite', label: "Did you cut your knuckle on someone's teeth (fight bite)?", weight: 8, urgent: true, type: 'radio', options: YN },
      { id: 'biteWound', label: 'Was this caused by an animal or human bite?', weight: 7, urgent: true, type: 'radio', options: YN },
      { id: 'crushInjury', label: 'Was the hand crushed (e.g., in a door or by a heavy object)?', weight: 5, urgent: false, type: 'radio', options: YN },
      { id: 'severeDeformity', label: 'Is the finger or hand severely out of shape?', weight: 6, urgent: true, type: 'radio', options: YN },
    ],
  },
  PELVIS: {
    thresholds: { critical: 10, urgent: 6, concerning: 5 },
    items: [
      { id: 'unableToWeightBear', label: 'Unable to put weight on leg', weight: 6, urgent: true, type: 'radio', options: YN },
    ],
  },
  LUMBAR_SPINE: {
    thresholds: { critical: 12, urgent: 7, concerning: 5 },
    items: [
      { id: 'unableToWalk', label: 'Are you unable to walk or stand because of your symptoms?', weight: 7, urgent: true, type: 'radio', options: YN },
      { id: 'ivDrugUse', label: 'Do you inject drugs?', weight: 5, urgent: false, type: 'radio', options: YN },
    ],
  },
  CERVICAL_SPINE: {
    thresholds: { critical: 12, urgent: 6, concerning: 5 },
    items: [
      { id: 'electricShockFeeling', label: 'Do you get an electric shock feeling down your spine when you bend your head forward?', weight: 6, urgent: false, type: 'radio', options: YN },
    ],
  },
};

export function getSupplementary(injuryType) {
  return SUPPLEMENTARY_RED_FLAGS[injuryType] || null;
}
